/*
 * Copyright (C) 2020-2024 The FrontDOOR Corp, Inc. All Rights Reserved.
 *
 * This software is the confidential and proprietary information of The FrontDOOR Corp, Inc.
 * ("Confidential Information").  You shall not disclose such Confidential Information and
 * shall use it only in accordance with the terms of the license agreement you entered into
 * with The FrontDOOR Corp, Inc.
 */

/**
 * Content and logic implementation for Dedicated Delivery landing page.
 *
 * @version 1.0 26th Feb 2024
 * @author gbiswajeeta
 */
import CachedIcon from "@mui/icons-material/Cached";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Box, Button, Grid, IconButton, Typography } from "@mui/material";
import { addDays, format } from "date-fns";
import {
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableInstance,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import "react-multi-date-picker/styles/colors/green.css";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import agent from "../../app/api/agent";
import getAxiosError from "../../app/api/axiosError";
import { getClientPerformanceData } from "../../app/features/dedicatedDelivery/dashboardSlice";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import { DdClientPerformanceResponse } from "../../app/models/responseModels/DdClientPerformanceResponse";
import GridDataFilter, {
  FilterableColumns,
  FilterableColumnsState,
} from "../../components/common/filter/GridDataFilter";
import Loader from "../../components/common/loader/Loader";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import { DD_COLUMN_NAME, DD_LABEL_NAME, ddClientGridFilter } from "./DdTypes";
import { getDefaultMRTOptions } from "./DefaultMrtOptions";
import { ImportRouteData } from "./ImportRouteData";
import { formatISODateString } from "../../components/common/utils/DateUtils";

const DedicatedDeliveryDashboard = () => {
  const defaultMRTOptions = getDefaultMRTOptions<DdClientPerformanceResponse>();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // get clientData from store
  const { isLoading, clientData } = useSelector(
    (state: RootState) => state.dedicatedDeliveryDashboard
  );

  const [clonedClientData, setClonedClientData] =
    useState<DdClientPerformanceResponse[]>(clientData);

  // keep startDate in state
  const [dateRange, setDateRange] = useState<DateObject[]>([
    new DateObject().setDate(addDays(new Date(), -30)),
    new DateObject().setDate(new Date()),
  ]);

  // keep flag to enabled page loader
  const [dataFetchig, setDataFetchig] = useState<boolean>(true);

  const [exporting, setExporting] = useState<boolean>(false);

  const [resetFilter, setResetFilter] = useState<boolean>(false);

  // set page title
  useEffect(() => {
    document.title = "Dedicated Delivery - Dashboard";
  }, []);

  useEffect(() => {
    setClonedClientData(clientData);
  }, [clientData]);

  // get landing page overview data
  const getDashboardOverviewData = useCallback(async () => {
    try {
      const response = await dispatch(
        getClientPerformanceData({
          startDate: dateRange[0].format("YYYY-MM-DD"),
          endDate: dateRange[1].format("YYYY-MM-DD"),
        })
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "clientData",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "clientDataException",
      });
    } finally {
      setDataFetchig(false);
      setResetFilter(false);
    }
  }, [dateRange]);

  // incase loader flag is enabled then fetch overview data for landing page
  useEffect(() => {
    if (dataFetchig) {
      getDashboardOverviewData();
    }
  }, [dataFetchig, getDashboardOverviewData]);

  // Columns to be used on grid on landing page
  const columns = useMemo<MRT_ColumnDef<DdClientPerformanceResponse>[]>(
    () => [
      {
        accessorKey: DD_COLUMN_NAME.CLIENTID,
        header: DD_LABEL_NAME.CLIENTID,
        size: 10,
        Cell: ({ cell, row }) => (
          <Button variant="contained">{row.original.clientId}</Button>
        ),
      },
      {
        accessorKey: DD_COLUMN_NAME.CLIENTNAME,
        header: DD_LABEL_NAME.CLIENTNAME,
        size: 10,
      },
      {
        accessorKey: DD_COLUMN_NAME.CLIENTCREATEDON,
        header: DD_LABEL_NAME.CLIENTCREATEDON,
        size: 10,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return "N/A";
          const formattedDate = formatISODateString(value);
          return formattedDate;
        },
      },
      {
        accessorKey: DD_COLUMN_NAME.TOTALSTORES,
        header: DD_LABEL_NAME.TOTALSTORES,
        size: 10,
      },
      {
        accessorKey: DD_COLUMN_NAME.TOTALROUTES,
        header: DD_LABEL_NAME.TOTALROUTES,
        size: 10,
      },
      {
        accessorKey: DD_COLUMN_NAME.PACKAGESTENDERED,
        header: DD_LABEL_NAME.PACKAGESTENDERED,
        size: 10,
      },
      {
        accessorKey: DD_COLUMN_NAME.ORDERSDELIVERED,
        header: DD_LABEL_NAME.ORDERSDELIVERED,
        size: 10,
      },
      {
        accessorKey: DD_COLUMN_NAME.UNDELIVEREDNOATTEMPT,
        header: DD_LABEL_NAME.UNDELIVEREDNOATTEMPT,
        size: 5,
      },
      {
        accessorKey: DD_COLUMN_NAME.UNDELIVEREDATTEMPT,
        header: DD_LABEL_NAME.UNDELIVEREDATTEMPT,
        size: 10,
      },
      {
        accessorKey: DD_COLUMN_NAME.GROSSPERFORMANCE,
        header: DD_LABEL_NAME.GROSSPERFORMANCE,
        size: 200,
      },
      {
        accessorKey: DD_COLUMN_NAME.NETPERFORMANCE,
        header: DD_LABEL_NAME.NETPERFORMANCE,
        size: 200,
      },
    ],
    []
  );

  const handleExportData = (rows: MRT_Row<DdClientPerformanceResponse>[]) => {
    const worksheet = XLSX.utils.json_to_sheet(rows.flatMap((e) => e.original));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Dates");
    XLSX.writeFile(
      workbook,
      "ClientGeneral" + format(new Date(), "yyyyMMddHHmmss") + ".xlsx",
      { compression: true }
    );
  };

  const handleLandingPageExport = async () => {
    try {
      setExporting(true);
      if (dateRange) {
        var formatStartDateP = dateRange[0].format("YYYY-MM-DD");
        var formatEndDateP = dateRange[1].format("YYYY-MM-DD");
        // Send GET request to Spring Boot backend
        const response =
          await agent.DedicatedDeliveryAgent.exportLandingPageData({
            startDate: formatStartDateP,
            endDate: formatEndDateP,
          });

        // Create a blob from the response data
        const blob = new Blob([response], { type: "text/csv" });

        // Create a download link and trigger click event
        const downloadLink = document.createElement("a");
        downloadLink.href = window.URL.createObjectURL(blob);
        downloadLink.download =
          "Dedicated-Delivery-Report-" +
          formatStartDateP +
          "-to-" +
          formatEndDateP +
          ".csv";
        document.body.appendChild(downloadLink);
        downloadLink.click();

        // Cleanup
        document.body.removeChild(downloadLink);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Error downloading file:" + error);
      // Handle error
    } finally {
      setExporting(false);
    }
  };

  // elements to be rendered on top of the grid
  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<DdClientPerformanceResponse>
  ) => {
    return (
      <Box>
        <CustomTooltip arrow title="Refresh" onClick={getDashboardOverviewData}>
          <IconButton type="button">
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Export" onClick={handleLandingPageExport}>
          <IconButton type="button">
            <FileDownloadOutlinedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
      </Box>
    );
  };

  // preprare MRT V2 grid
  const table = useMaterialReactTable({
    ...defaultMRTOptions,
    columns: columns,
    data: clonedClientData,
    initialState: {
      ...defaultMRTOptions.initialState,
      columnVisibility: {
        clientId: true,
      },
      sorting: [{ desc: true, id: "clientId" }],
    },
    state: {
      showProgressBars: isLoading,
      sorting: [{ desc: true, id: "clientId" }],
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {
        const clientId = row.original.clientId;
        const clientName = row.original.clientName;
        const totalStores = row.original.totalStores;
        console.log(clientId);
        /* navigate("../dedicated-delivery-overview", {
          state: {
            client_id: clientId,
            client_name: clientName,
            total_stores_served: totalStores,
            start_date: startDateVal,
            end_date: endDateVal,
          },
          replace: true,
        }); */

        // native to Actions page with parameters
        navigate({
          pathname: "../dedicated-delivery-overview",
          search: `?client_id=${clientId}&client_name=${clientName}&start_date=${dateRange[0].format(
            "YYYY-MM-DD"
          )}&end_date=${dateRange[1].format("YYYY-MM-DD")}`,
        });
      },
      ...defaultMRTOptions.muiTableBodyRowProps,
      sx: {
        cursor: "pointer",
      },
    }),
    renderTopToolbarCustomActions: ({ table }) =>
      renderTopToolbarCustomActions(table),
    renderBottomToolbarCustomActions: () => {
      return (
        <Typography variant="subtitle2" component="p">
          Total Rows: {clonedClientData.length}
        </Typography>
      );
    },
  });

  return (
    <Grid
      container
      paddingTop={2}
      rowSpacing={2}
      justifyContent="space-between"
    >
      <Loader isLoading={exporting} />
      <Grid item>
        <Grid container item alignItems="center">
          <Box zIndex={100}>
            <DatePicker
              format="MM/DD/YYYY"
              className="green"
              dateSeparator=" ~ "
              range
              rangeHover
              maxDate={new Date()}
              value={dateRange}
              onChange={(dateObjects: DateObject[]) => {
                setDateRange(dateObjects);
              }}
            />
          </Box>
          <Button
            size="large"
            onClick={() => {
              setDataFetchig(true);
              setResetFilter(true);
            }}
          >
            SUBMIT
          </Button>
        </Grid>
      </Grid>
      <Grid item>
        <ImportRouteData clientId={-1} />
      </Grid>
      <Grid item xs={12}>
        <GridDataFilter
          filterableColumns={ddClientGridFilter}
          title="Search Client Data"
          dateRange={dateRange}
          resetFlag={resetFilter}
          asyncSubmitFunction={(filterState: FilterableColumnsState) => {
            if (Object.keys(filterState).length) {
              const filteredData = clientData.filter(
                (client: Record<string, any>) => {
                  for (const key in filterState) {
                    if (filterState.hasOwnProperty(key)) {
                      const filterValues = filterState[key].values;
                      if (key === "clientCreatedOn") {
                        if (filterValues.length === 2) {
                          const clientCreatedDate = new Date(client[key]);
                          const startDate = new Date(filterValues[0]);
                          const endDate = new Date(filterValues[1]);

                          endDate.setHours(23, 59, 59, 999);

                          const isDateInRange =
                            clientCreatedDate >= startDate &&
                            clientCreatedDate <= endDate;
                          if (!isDateInRange) {
                            return false;
                          }
                        }
                      } else {
                        if (
                          filterValues.length > 0 &&
                          !filterValues.includes(client[key])
                        ) {
                          return false;
                        }
                      }
                    }
                  }
                  return true;
                }
              );

              console.log(filterState, clientData, filteredData);
              setClonedClientData(filteredData);
            } else {
              setClonedClientData(clientData);
            }
          }}
          asyncSelectedColumnValues={(
            columnName: string,
            setClFilterableColumns: React.Dispatch<
              React.SetStateAction<FilterableColumns>
            >
          ) => {
            const filteredData = clientData
              .map((e: Record<string, any>) => e[columnName])
              .filter((value) => value !== undefined && value !== null);
            setClFilterableColumns((prevState: FilterableColumns) => {
              return {
                ...prevState,
                [columnName]: {
                  ...prevState[columnName],
                  options: Array.from(new Set(filteredData)) ?? [],
                },
              };
            });
          }}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <MaterialReactTable table={table} />
      </Grid>
    </Grid>
  );
};

export default DedicatedDeliveryDashboard;
